import React, { useEffect, useRef, useState } from 'react';
import { Trash2 } from 'lucide-react';
import { EditorApi } from './commands';
import { Field, NumberField, SelectField, Toggle } from './ui';
import {
  BorderSpec, DesignerNode, DesignerStyle, FONT_FAMILIES, ListMarker, MAX_SIGNATURE_PARTIES, PAGE_SIZES, PageSize,
  resolveStyle, SignatureParty, uid,
} from './model';
import { clampPerRow, DEFAULT_SIGN_HEIGHT, newSignatureParty } from './signatureBlock';

type Tab = 'text' | 'paragraph' | 'element' | 'page' | 'styles';
const TABS: { id: Tab; label: string }[] = [
  { id: 'text', label: 'Text' },
  { id: 'paragraph', label: 'Absatz' },
  { id: 'element', label: 'Element' },
  { id: 'page', label: 'Seite' },
  { id: 'styles', label: 'Vorlagen' },
];

/** Eigenschaftenbereich: Zeichen-, Absatz-, Element-, Seiten- und Vorlagenformate. */
export default function Inspector({ api }: { api: EditorApi }) {
  const [tab, setTab] = useState<Tab>('paragraph');
  const node = api.node;
  const style = api.style;
  const setStyle = (patch: DesignerStyle) => api.setStyle(patch);
  const lastNodeId = useRef<string | undefined>(undefined);

  // Beim Auswählen eines Sonderelements direkt dessen Einstellungen zeigen.
  useEffect(() => {
    if (!node || node.id === lastNodeId.current) return;
    lastNodeId.current = node.id;
    const hasOwnSettings = !['paragraph', 'heading'].includes(node.type);
    setTab(current => (hasOwnSettings ? 'element' : current === 'element' ? 'paragraph' : current));
  }, [node]);

  return (
    <aside className="bg-white border border-gray-200 rounded-lg h-fit sticky top-32" aria-label="Eigenschaften">
      <div className="flex border-b border-gray-200 text-xs" role="tablist" aria-label="Eigenschaftenbereiche">
        {TABS.map(entry => (
          <button
            key={entry.id}
            type="button"
            role="tab"
            aria-selected={tab === entry.id}
            onClick={() => setTab(entry.id)}
            className={`flex-1 py-2 ${tab === entry.id ? 'border-b-2 border-primary-500 text-primary-700 font-medium' : 'text-gray-500 hover:text-gray-700'}`}
          >
            {entry.label}
          </button>
        ))}
      </div>
      <div className="p-3 space-y-3 max-h-[70vh] overflow-auto">
        {tab === 'text' && <TextPanel api={api} />}
        {tab === 'paragraph' && (node ? <ParagraphPanel api={api} style={style} setStyle={setStyle} /> : <Hint />)}
        {tab === 'element' && (node ? <ElementPanel api={api} node={node} /> : <Hint />)}
        {tab === 'page' && <PagePanel api={api} />}
        {tab === 'styles' && <StylePanel api={api} />}
      </div>
    </aside>
  );
}

const Hint = () => <p className="text-xs text-gray-500">Element auswählen, um seine Eigenschaften zu bearbeiten.</p>;

function TextPanel({ api }: { api: EditorApi }) {
  const { flags, style } = api;
  const set = (key: any, value: any) => (api.hasSelection ? api.setMark(key, value) : api.setStyle({ [key]: value }));
  return (
    <>
      <p className="text-[11px] text-gray-500">{api.hasSelection ? 'Änderungen gelten für die markierte Textstelle.' : 'Ohne Markierung gilt die Änderung für das ganze Element.'}</p>
      <SelectField
        label="Schriftart"
        value={(flags.fontFamily || style.fontFamily || 'Helvetica') as string}
        options={FONT_FAMILIES.map(font => ({ value: font.value, label: font.label }))}
        onChange={value => set('fontFamily', value)}
      />
      <NumberField label="Schriftgröße" unit="pt" min={4} max={200} step={0.5} value={flags.fontSize ?? style.fontSize} onChange={value => set('fontSize', value)} />
      <NumberField label="Zeichenabstand" unit="px" min={-5} max={20} step={0.1} value={flags.letterSpacing ?? style.letterSpacing} onChange={value => set('letterSpacing', value)} />
      <Field label="Textfarbe">
        <input type="color" aria-label="Textfarbe wählen" className="h-8 w-full border rounded" value={flags.color || style.color || '#111827'} onChange={event => set('color', event.target.value)} />
      </Field>
      <Field label="Hintergrundfarbe">
        <input type="color" aria-label="Hintergrundfarbe wählen" className="h-8 w-full border rounded" value={flags.background || style.background || '#ffffff'} onChange={event => set('background', event.target.value)} />
      </Field>
      <SelectField
        label="Textumwandlung"
        value={style.textTransform || 'none'}
        options={[{ value: 'none', label: 'Ohne' }, { value: 'uppercase', label: 'GROSSBUCHSTABEN' }, { value: 'lowercase', label: 'kleinbuchstaben' }, { value: 'capitalize', label: 'Wortanfänge Groß' }]}
        onChange={value => api.setStyle({ textTransform: value as any })}
      />
      <Field label="Verknüpfung">
        <input
          type="url"
          aria-label="Verknüpfung"
          placeholder="https://"
          className="w-full border border-gray-300 rounded p-1.5 text-sm"
          value={flags.link || ''}
          onChange={event => api.setMark('link', event.target.value || undefined)}
        />
      </Field>
    </>
  );
}

function ParagraphPanel({ api, style, setStyle }: { api: EditorApi; style: DesignerStyle; setStyle: (patch: DesignerStyle) => void }) {
  return (
    <>
      <SelectField label="Ausrichtung" value={style.align || 'left'} options={[{ value: 'left', label: 'Linksbündig' }, { value: 'center', label: 'Zentriert' }, { value: 'right', label: 'Rechtsbündig' }, { value: 'justify', label: 'Blocksatz' }]} onChange={value => setStyle({ align: value as any })} />
      <NumberField label="Zeilenabstand" min={0.5} max={5} step={0.05} value={style.lineHeight} onChange={value => setStyle({ lineHeight: value })} />
      <div className="grid grid-cols-2 gap-2">
        <NumberField label="Abstand davor" unit="pt" max={400} value={style.marginTop} onChange={value => setStyle({ marginTop: value })} />
        <NumberField label="Abstand danach" unit="pt" max={400} value={style.marginBottom} onChange={value => setStyle({ marginBottom: value })} />
      </div>
      <div className="grid grid-cols-2 gap-2">
        <NumberField label="Einzug links" unit="pt" max={400} value={style.indentLeft} onChange={value => setStyle({ indentLeft: value })} />
        <NumberField label="Einzug rechts" unit="pt" max={400} value={style.indentRight} onChange={value => setStyle({ indentRight: value })} />
      </div>
      <NumberField label="Erstzeileneinzug" unit="pt" min={-400} max={400} value={style.firstLineIndent} onChange={value => setStyle({ firstLineIndent: value })} hint="Negative Werte ergeben einen hängenden Einzug." />
      <NumberField label="Innenabstand" unit="pt" max={200} value={style.padding} onChange={value => setStyle({ padding: value })} />
      <BorderEditor style={style} setStyle={setStyle} />
      <div className="pt-2 border-t border-gray-100 space-y-2">
        <Toggle label="Seitenumbruch davor" checked={!!style.pageBreakBefore} onChange={checked => setStyle({ pageBreakBefore: checked })} />
        <Toggle label="Seitenumbruch danach" checked={!!style.pageBreakAfter} onChange={checked => setStyle({ pageBreakAfter: checked })} />
        <Toggle label="Nicht vom nächsten Element trennen" checked={!!style.keepWithNext} onChange={checked => setStyle({ keepWithNext: checked })} />
        <Toggle label="Element zusammenhalten" checked={!!style.keepTogether} onChange={checked => setStyle({ keepTogether: checked })} hint="Verhindert einen Umbruch innerhalb des Elements." />
      </div>
      {api.node?.type === 'heading' && (
        <div className="pt-2 border-t border-gray-100 space-y-2">
          <SelectField label="Ebene" value={api.node.level || 1} options={[1, 2, 3, 4, 5, 6].map(level => ({ value: level, label: `Überschrift ${level}` }))} onChange={value => api.convert('heading', Number(value) as any)} />
          <Toggle label="In der Nummerierung berücksichtigen" checked={api.node.numbered !== false} onChange={checked => api.setDocumentNode({ numbered: checked })} />
        </div>
      )}
    </>
  );
}

function BorderEditor({ style, setStyle }: { style: DesignerStyle; setStyle: (patch: DesignerStyle) => void }) {
  const sides: { key: 'top' | 'right' | 'bottom' | 'left'; label: string }[] = [
    { key: 'top', label: 'Oben' }, { key: 'right', label: 'Rechts' }, { key: 'bottom', label: 'Unten' }, { key: 'left', label: 'Links' },
  ];
  const update = (side: 'top' | 'right' | 'bottom' | 'left', patch: Partial<BorderSpec>) =>
    setStyle({ borders: { ...(style.borders || {}), [side]: { ...(style.borders?.[side] || {}), ...patch } } });
  return (
    <div className="pt-2 border-t border-gray-100">
      <span className="block text-xs font-medium text-gray-700 mb-2">Rahmen</span>
      <div className="space-y-1.5">
        {sides.map(side => (
          <div key={side.key} className="flex items-center gap-2">
            <span className="text-[11px] w-12 text-gray-600">{side.label}</span>
            <input
              type="number"
              aria-label={`Rahmen ${side.label} Stärke`}
              min={0}
              max={20}
              step={0.5}
              className="w-14 border border-gray-300 rounded p-1 text-xs"
              value={style.borders?.[side.key]?.width ?? 0}
              onChange={event => update(side.key, { width: Number(event.target.value) })}
            />
            <select
              aria-label={`Rahmen ${side.label} Art`}
              className="border border-gray-300 rounded p-1 text-xs"
              value={style.borders?.[side.key]?.style || 'solid'}
              onChange={event => update(side.key, { style: event.target.value as any })}
            >
              <option value="solid">durchgezogen</option>
              <option value="dashed">gestrichelt</option>
              <option value="dotted">gepunktet</option>
            </select>
            <input
              type="color"
              aria-label={`Rahmen ${side.label} Farbe`}
              className="h-6 w-8 border rounded"
              value={style.borders?.[side.key]?.color || '#111827'}
              onChange={event => update(side.key, { color: event.target.value })}
            />
          </div>
        ))}
      </div>
      <NumberField label="Eckenradius" unit="px" max={60} value={style.borderRadius} onChange={value => setStyle({ borderRadius: value })} />
    </div>
  );
}

/** Anzahl, Anordnung und Namen der Vertragsparteien eines Unterschriftenblocks. */
function SignaturePartiesPanel({ node, set }: { node: DesignerNode; set: (patch: Partial<DesignerNode>) => void }) {
  const parties: SignatureParty[] = node.parties || [];
  const rename = (id: string, name: string) => set({ parties: parties.map(entry => (entry.id === id ? { ...entry, name } : entry)) });
  const setCount = (value?: number) => {
    const total = Math.max(1, Math.min(MAX_SIGNATURE_PARTIES, Math.round(value || 1)));
    if (total === parties.length) return;
    set({
      parties: total < parties.length
        ? parties.slice(0, total)
        : [...parties, ...Array.from({ length: total - parties.length }, () => newSignatureParty())],
    });
  };

  return (
    <>
      <NumberField label="Anzahl der Vertragsparteien" min={1} max={MAX_SIGNATURE_PARTIES} value={parties.length} onChange={setCount} />
      <SelectField
        label="Unterschriften je Zeile"
        value={clampPerRow(node.perRow)}
        options={[1, 2, 3, 4].map(value => ({ value, label: String(value) }))}
        onChange={value => set({ perRow: Number(value) })}
      />
      <NumberField
        label="Höhe des Unterschriftsfelds"
        unit="pt"
        min={0}
        max={300}
        value={node.signHeight ?? DEFAULT_SIGN_HEIGHT}
        onChange={value => set({ signHeight: value ?? DEFAULT_SIGN_HEIGHT })}
        hint="Freier Raum über der Unterschriftslinie."
      />
      <div className="pt-2 border-t border-gray-100 space-y-2">
        <span className="block text-xs font-medium text-gray-700">Namen der Vertragsparteien</span>
        {parties.map((party, index) => (
          <div key={party.id} className="flex items-center gap-1">
            <span className="w-5 shrink-0 text-right text-[11px] text-gray-400">{index + 1}.</span>
            <input
              className="flex-1 border border-gray-300 rounded p-1.5 text-sm"
              aria-label={`Vertragspartei ${index + 1}`}
              placeholder="Name der Vertragspartei"
              value={party.name || ''}
              onChange={event => rename(party.id, event.target.value)}
            />
            <button
              type="button"
              aria-label={`Vertragspartei ${index + 1} entfernen`}
              disabled={parties.length <= 1}
              className="text-gray-400 hover:text-red-600 disabled:opacity-30"
              onClick={() => set({ parties: parties.filter(entry => entry.id !== party.id) })}
            >
              <Trash2 size={13} />
            </button>
          </div>
        ))}
        <button
          type="button"
          className="text-xs border rounded px-2 py-1 hover:bg-gray-50 disabled:opacity-40"
          disabled={parties.length >= MAX_SIGNATURE_PARTIES}
          onClick={() => set({ parties: [...parties, newSignatureParty()] })}
        >
          Vertragspartei hinzufügen
        </button>
      </div>
    </>
  );
}

function ElementPanel({ api, node }: { api: EditorApi; node: DesignerNode }) {
  const set = (patch: Partial<DesignerNode>) => api.setDocumentNode(patch);
  if (node.type === 'qr') return <QrPanel node={node} set={set} />;
  if (node.type === 'signature') {
    return (
      <>
        <Field label="Beschriftung"><input className="w-full border border-gray-300 rounded p-1.5 text-sm" aria-label="Beschriftung" value={node.label || ''} onChange={event => set({ label: event.target.value })} /></Field>
        <Field label="Rolle"><input className="w-full border border-gray-300 rounded p-1.5 text-sm" aria-label="Rolle" placeholder="z. B. Mieter" value={node.role || ''} onChange={event => set({ role: event.target.value })} /></Field>
        <SelectField label="Signaturart" value={node.provider || 'LOCAL'} options={[{ value: 'LOCAL', label: 'Handschriftlich' }, { value: 'SKRIBBLE', label: 'Skribble' }, { value: 'ATRUST', label: 'ID Austria' }]} onChange={value => set({ provider: value })} />
        <Toggle label="Ort mitdrucken" checked={node.showPlace !== false} onChange={checked => set({ showPlace: checked })} />
        <Toggle label="Datum mitdrucken" checked={node.showDate !== false} onChange={checked => set({ showDate: checked })} />
      </>
    );
  }
  if (node.type === 'signatureBlock') return <SignaturePartiesPanel node={node} set={set} />;
  if (node.type === 'image') {
    return (
      <>
        <Field label="Bildquelle"><input className="w-full border border-gray-300 rounded p-1.5 text-sm" aria-label="Bildadresse" placeholder="https:// oder eingebettet" value={node.src?.startsWith('data:') ? '(eingebettetes Bild)' : node.src || ''} onChange={event => set({ src: event.target.value })} /></Field>
        <Field label="Alternativtext"><input className="w-full border border-gray-300 rounded p-1.5 text-sm" aria-label="Alternativtext" value={node.alt || ''} onChange={event => set({ alt: event.target.value })} /></Field>
        <Field label="Bildunterschrift"><input className="w-full border border-gray-300 rounded p-1.5 text-sm" aria-label="Bildunterschrift" value={node.caption || ''} onChange={event => set({ caption: event.target.value })} /></Field>
        <div className="grid grid-cols-2 gap-2">
          <NumberField label="Breite" unit="px" min={1} max={2000} value={node.width} onChange={value => set({ width: value })} />
          <NumberField label="Höhe" unit="px" min={1} max={2000} value={node.height} onChange={value => set({ height: value })} />
        </div>
      </>
    );
  }
  if (node.type === 'list') {
    const markers: { value: ListMarker; label: string }[] = node.ordered
      ? [{ value: 'decimal', label: '1. 2. 3.' }, { value: 'lower-alpha', label: 'a. b. c.' }, { value: 'upper-alpha', label: 'A. B. C.' }, { value: 'lower-roman', label: 'i. ii. iii.' }, { value: 'upper-roman', label: 'I. II. III.' }]
      : [{ value: 'disc', label: '• Punkt' }, { value: 'circle', label: '◦ Kreis' }, { value: 'square', label: '▪ Quadrat' }, { value: 'dash', label: '– Strich' }];
    return (
      <>
        <Toggle label="Nummerierte Liste" checked={!!node.ordered} onChange={checked => set({ ordered: checked, marker: undefined })} />
        <SelectField label="Aufzählungszeichen" value={node.marker || markers[0].value} options={markers} onChange={value => set({ marker: value as ListMarker })} />
        {node.ordered && <NumberField label="Startwert" min={1} max={9999} value={node.start ?? 1} onChange={value => set({ start: value ?? 1 })} />}
        <p className="text-[11px] text-gray-500">Tab und Umschalt+Tab ändern die Ebene eines Eintrags.</p>
      </>
    );
  }
  if (node.type === 'table') {
    const columns: any[] = node.columns || [];
    return (
      <>
        <Toggle label="Kopfzeile anzeigen" checked={node.headerRow !== false} onChange={checked => set({ headerRow: checked })} />
        <Toggle label="Zeilen abwechselnd einfärben" checked={!!node.zebra} onChange={checked => set({ zebra: checked })} />
        <Field label="Rahmenfarbe"><input type="color" aria-label="Tabellenrahmenfarbe" className="h-8 w-full border rounded" value={node.borderColor || '#9ca3af'} onChange={event => set({ borderColor: event.target.value })} /></Field>
        <div className="pt-2 border-t border-gray-100 space-y-2">
          <span className="block text-xs font-medium text-gray-700">Spalten</span>
          {columns.map(column => (
            <div key={column.id} className="border border-gray-200 rounded p-2 space-y-1.5">
              <div className="text-[11px] font-medium text-gray-600">{column.key} · {column.label}</div>
              <div className="grid grid-cols-2 gap-1.5">
                <select aria-label={`Datentyp ${column.key}`} className="border rounded p-1 text-xs" value={column.valueType} onChange={event => set({ columns: columns.map(entry => (entry.id === column.id ? { ...entry, valueType: event.target.value } : entry)) })}>
                  {['TEXT', 'NUMBER', 'CURRENCY', 'PERCENT', 'DATE', 'FORMULA'].map(type => <option key={type} value={type}>{type}</option>)}
                </select>
                <select aria-label={`Ausrichtung ${column.key}`} className="border rounded p-1 text-xs" value={column.align || 'left'} onChange={event => set({ columns: columns.map(entry => (entry.id === column.id ? { ...entry, align: event.target.value } : entry)) })}>
                  <option value="left">links</option><option value="center">zentriert</option><option value="right">rechts</option>
                </select>
                <input type="number" aria-label={`Breite ${column.key}`} min={1} max={100} className="border rounded p-1 text-xs" value={column.width ?? ''} placeholder="Breite %" onChange={event => set({ columns: columns.map(entry => (entry.id === column.id ? { ...entry, width: event.target.value === '' ? undefined : Number(event.target.value) } : entry)) })} />
                <input type="number" aria-label={`Nachkommastellen ${column.key}`} min={0} max={6} className="border rounded p-1 text-xs" value={column.decimals ?? ''} placeholder="Dez." onChange={event => set({ columns: columns.map(entry => (entry.id === column.id ? { ...entry, decimals: event.target.value === '' ? undefined : Number(event.target.value) } : entry)) })} />
              </div>
            </div>
          ))}
        </div>
      </>
    );
  }
  if (node.type === 'container') {
    const columns: any[] = node.columns || [];
    const distribute = (count: number) => api.setDocumentNode({
      columns: Array.from({ length: count }, (_unused, index) => columns[index] || { id: uid(), width: 0, nodes: [] }).map(column => ({ ...column, width: Math.round(100 / count) })),
    });
    return (
      <>
        <SelectField label="Spaltenanzahl" value={columns.length} options={[1, 2, 3, 4].map(count => ({ value: count, label: `${count} Spalten` }))} onChange={value => distribute(Number(value))} />
        <NumberField label="Spaltenabstand" unit="px" max={80} value={node.style?.columnGap ?? 12} onChange={value => api.setStyle({ columnGap: value })} />
        <SelectField label="Vertikale Ausrichtung" value={node.style?.verticalAlign || 'top'} options={[{ value: 'top', label: 'oben' }, { value: 'middle', label: 'mittig' }, { value: 'bottom', label: 'unten' }]} onChange={value => api.setStyle({ verticalAlign: value as any })} />
        {columns.map((column, index) => (
          <NumberField
            key={column.id}
            label={`Breite Spalte ${index + 1}`}
            unit="%"
            min={5}
            max={100}
            value={column.width}
            onChange={value => api.setDocumentNode({ columns: columns.map(entry => (entry.id === column.id ? { ...entry, width: value ?? 50 } : entry)) })}
          />
        ))}
      </>
    );
  }
  if (node.type === 'spacer') return <NumberField label="Höhe" unit="pt" min={0} max={800} value={node.height} onChange={value => set({ height: value ?? 24 })} />;
  if (node.type === 'toc') {
    return (
      <>
        <Field label="Titel"><input className="w-full border border-gray-300 rounded p-1.5 text-sm" aria-label="Titel des Inhaltsverzeichnisses" value={node.title || ''} onChange={event => set({ title: event.target.value })} /></Field>
        <SelectField label="Tiefe" value={node.maxLevel || 3} options={[1, 2, 3, 4, 5, 6].map(level => ({ value: level, label: `bis Ebene ${level}` }))} onChange={value => set({ maxLevel: Number(value) })} />
      </>
    );
  }
  return <p className="text-xs text-gray-500">Für dieses Element gibt es keine eigenen Einstellungen.</p>;
}

function QrPanel({ node, set }: { node: DesignerNode; set: (patch: Partial<DesignerNode>) => void }) {
  const type = node.payload?.type || 'URL';
  const setPayload = (key: string, value: any) => set({ payload: { ...node.payload, [key]: value } });
  const text = (placeholder: string, key: string, extra?: Partial<React.InputHTMLAttributes<HTMLInputElement>>) => (
    <input className="w-full border border-gray-300 rounded p-1.5 text-sm" placeholder={placeholder} value={node.payload?.[key] ?? ''} onChange={event => setPayload(key, event.target.value)} {...extra} />
  );
  return (
    <>
      <SelectField label="QR-Code-Typ" value={type} options={[{ value: 'URL', label: 'Link' }, { value: 'VCARD', label: 'vCard' }, { value: 'EPC', label: 'Onlinebanking' }]} onChange={value => set({ payload: { type: value } })} />
      {type === 'URL' && text('https://', 'url')}
      {type === 'VCARD' && (
        <>
          {text('Vorname (optional)', 'firstName')}
          {text('Nachname (optional)', 'lastName')}
          {text('Firmenname (optional)', 'organization')}
          {text('Telefonnummer (optional)', 'phone')}
          {text('E-Mail (optional)', 'email')}
        </>
      )}
      {type === 'EPC' && (
        <>
          {text('Empfänger', 'name')}
          {text('IBAN', 'iban')}
          <input type="number" className="w-full border border-gray-300 rounded p-1.5 text-sm" placeholder="Betrag" value={node.payload?.amount ?? ''} onChange={event => setPayload('amount', event.target.value === '' ? undefined : Number(event.target.value))} />
          {text('Zahlungsgrund (optional)', 'purpose', { maxLength: 140 })}
        </>
      )}
      <NumberField label="Kantenlänge" unit="px" min={40} max={300} value={node.size ?? 90} onChange={value => set({ size: value ?? 90 })} />
      <Field label="Bildunterschrift"><input className="w-full border border-gray-300 rounded p-1.5 text-sm" aria-label="QR-Bildunterschrift" value={node.caption || ''} onChange={event => set({ caption: event.target.value })} /></Field>
    </>
  );
}

function PagePanel({ api }: { api: EditorApi }) {
  const page = api.document.page || { size: 'A4' as PageSize, orientation: 'portrait' as const, marginTop: 20, marginRight: 20, marginBottom: 20, marginLeft: 25, columns: 1, columnGap: 8 };
  const set = (patch: Partial<typeof page>) => api.setDocument({ page: { ...page, ...patch } });
  return (
    <>
      <SelectField label="Seitenformat" value={page.size} options={(Object.keys(PAGE_SIZES) as PageSize[]).map(size => ({ value: size, label: `${PAGE_SIZES[size].label} (${PAGE_SIZES[size].width} × ${PAGE_SIZES[size].height} mm)` }))} onChange={value => set({ size: value as PageSize })} />
      <SelectField label="Ausrichtung" value={page.orientation} options={[{ value: 'portrait', label: 'Hochformat' }, { value: 'landscape', label: 'Querformat' }]} onChange={value => set({ orientation: value as any })} />
      <div className="grid grid-cols-2 gap-2">
        <NumberField label="Rand oben" unit="mm" max={100} value={page.marginTop} onChange={value => set({ marginTop: value ?? 20 })} />
        <NumberField label="Rand unten" unit="mm" max={100} value={page.marginBottom} onChange={value => set({ marginBottom: value ?? 20 })} />
        <NumberField label="Rand links" unit="mm" max={100} value={page.marginLeft} onChange={value => set({ marginLeft: value ?? 25 })} />
        <NumberField label="Rand rechts" unit="mm" max={100} value={page.marginRight} onChange={value => set({ marginRight: value ?? 20 })} />
      </div>
      <SelectField label="Textspalten" value={page.columns || 1} options={[1, 2, 3].map(count => ({ value: count, label: count === 1 ? 'einspaltig' : `${count}-spaltig` }))} onChange={value => set({ columns: Number(value) })} />
      <NumberField label="Spaltenabstand" unit="mm" max={50} value={page.columnGap ?? 8} onChange={value => set({ columnGap: value ?? 8 })} />
      <div className="pt-2 border-t border-gray-100 space-y-2">
        <SelectField
          label="Überschriften nummerieren"
          value={api.document.settings?.headingNumbering || 'none'}
          options={[{ value: 'none', label: 'keine Nummerierung' }, { value: 'decimal', label: '1. / 1.1 / 1.1.1' }, { value: 'paragraph', label: '§1, §2 …' }]}
          onChange={value => api.setDocument({ settings: { ...api.document.settings, headingNumbering: value as any } })}
        />
        {api.document.settings?.headingNumbering === 'paragraph' && (
          <Field label="Zeichen vor der Nummer">
            <input className="w-full border border-gray-300 rounded p-1.5 text-sm" aria-label="Nummerierungszeichen" value={api.document.settings?.numberingPrefix ?? '§'} onChange={event => api.setDocument({ settings: { ...api.document.settings, numberingPrefix: event.target.value } })} />
          </Field>
        )}
      </div>
      <div className="pt-2 border-t border-gray-100 space-y-2">
        <span className="block text-xs font-medium text-gray-700">Dokumentvorgabe (gilt für alle Elemente)</span>
        <SelectField label="Grundschrift" value={api.document.defaults?.fontFamily || 'Helvetica'} options={FONT_FAMILIES.map(font => ({ value: font.value, label: font.label }))} onChange={value => api.setDocument({ defaults: { ...api.document.defaults, fontFamily: value } })} />
        <NumberField label="Grundschriftgröße" unit="pt" min={4} max={40} step={0.5} value={api.document.defaults?.fontSize ?? 10} onChange={value => api.setDocument({ defaults: { ...api.document.defaults, fontSize: value } })} />
        <NumberField label="Grundzeilenabstand" min={0.8} max={3} step={0.05} value={api.document.defaults?.lineHeight ?? 1.4} onChange={value => api.setDocument({ defaults: { ...api.document.defaults, lineHeight: value } })} />
      </div>
    </>
  );
}

function StylePanel({ api }: { api: EditorApi }) {
  const styles = api.document.styles || [];
  const [name, setName] = useState('');
  const active = api.node?.style?.styleRef;
  return (
    <>
      <p className="text-[11px] text-gray-500">Formatvorlagen bündeln Schrift-, Absatz- und Rahmeneinstellungen und lassen sich auf beliebig viele Elemente anwenden.</p>
      <div className="flex gap-1">
        <input className="flex-1 border border-gray-300 rounded p-1.5 text-sm" aria-label="Name der Formatvorlage" placeholder="Neue Vorlage benennen" value={name} onChange={event => setName(event.target.value)} />
        <button type="button" disabled={!name.trim() || !api.node} onClick={() => { api.createStyleFromNode(name.trim()); setName(''); }} className="px-3 py-1.5 bg-primary-600 text-white rounded text-sm disabled:opacity-40">Anlegen</button>
      </div>
      {styles.length === 0 && <p className="text-xs text-gray-500">Noch keine eigenen Formatvorlagen.</p>}
      {styles.map(entry => {
        const preview = resolveStyle(api.document, entry.style);
        return (
          <div key={entry.id} className={`border rounded p-2 ${active === entry.id ? 'border-primary-400 bg-primary-50' : 'border-gray-200'}`}>
            <div className="flex items-center justify-between gap-2">
              <button type="button" className="text-sm text-left flex-1 truncate" onClick={() => api.applyStyleRef(entry.id)} style={{ fontWeight: preview.bold ? 700 : undefined, fontStyle: preview.italic ? 'italic' : undefined, color: preview.color }}>
                {entry.name}
              </button>
              <button type="button" aria-label={`Formatvorlage ${entry.name} aktualisieren`} className="text-[11px] text-primary-600" onClick={() => api.updateStyleFromNode(entry.id)}>aktualisieren</button>
              <button type="button" aria-label={`Formatvorlage ${entry.name} löschen`} className="text-gray-400 hover:text-red-600" onClick={() => api.setDocument({ styles: styles.filter(item => item.id !== entry.id) })}><Trash2 size={13} /></button>
            </div>
            <div className="text-[11px] text-gray-500 mt-1">{preview.fontFamily} · {preview.fontSize} pt · Zeilenabstand {preview.lineHeight}</div>
          </div>
        );
      })}
      {active && <button type="button" className="w-full border rounded p-1.5 text-xs" onClick={() => api.applyStyleRef(undefined)}>Formatvorlage vom Element lösen</button>}
    </>
  );
}
